import { useEffect, useState } from "react";
import api, { formatMoney, formatDate, daysUntil, urgencyClass } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Car, Plus, Trash, PencilSimple, PlusCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import MoneyInput from "@/components/MoneyInput";
import IbanInput from "@/components/IbanInput";

const emptyLoan = { company_id: "", ad: "", banka: "", iban: "", aciklama: "", currency: "TRY", taksitler: [] };
const TAKSIT_SAYILARI = [3, 6, 9, 12, 18, 24, 30, 36, 48, 60, 72, 84];

export default function LoansPage() {
  const { companies, selectedCompany } = useCompany();
  const [loans, setLoans] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyLoan);
  const [editingId, setEditingId] = useState(null);

  // Otomatik taksit üretimi için
  const [totalAmount, setTotalAmount] = useState(0);
  const [taksitSayisi, setTaksitSayisi] = useState(12);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    const q = selectedCompany === "all" ? "" : `?company_id=${selectedCompany}`;
    const { data } = await api.get(`/loans${q}`);
    setLoans(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedCompany]);

  const openNew = () => {
    setForm({ ...emptyLoan, company_id: selectedCompany !== "all" ? selectedCompany : companies[0]?.id || "" });
    setEditingId(null);
    setTotalAmount(0);
    setTaksitSayisi(12);
    setStartDate(new Date().toISOString().slice(0, 10));
    setDialog(true);
  };

  const openEdit = (l) => {
    setForm({ ...l, iban: l.iban || "", taksitler: [...l.taksitler] });
    setEditingId(l.id);
    setDialog(true);
  };

  const save = async () => {
    if (!form.company_id) { toast.error("Şirket seçin"); return; }
    if (!form.ad) { toast.error("Kredi adı girin"); return; }
    try {
      if (editingId) await api.put(`/loans/${editingId}`, form);
      else await api.post("/loans", form);
      toast.success("Kaydedildi");
      setDialog(false);
      load();
    } catch { toast.error("Kayıt hatası"); }
  };

  const remove = async (id) => {
    if (!confirm("Bu krediyi tüm taksitleriyle silmek istediğinize emin misiniz?")) return;
    await api.delete(`/loans/${id}`);
    toast.success("Silindi");
    load();
  };

  const toggle = async (loan, idx) => {
    const updated = { ...loan };
    updated.taksitler = loan.taksitler.map((t, i) => i === idx ? { ...t, odendi: !t.odendi } : t);
    await api.put(`/loans/${loan.id}`, updated);
    load();
  };

  const generateInstallments = () => {
    if (totalAmount <= 0 || taksitSayisi <= 0 || !startDate) {
      toast.error("Toplam tutar, taksit sayısı ve başlangıç tarihi girin");
      return;
    }
    const per = Math.round((totalAmount / taksitSayisi) * 100) / 100;
    const rem = Math.round((totalAmount - per * (taksitSayisi - 1)) * 100) / 100;
    const [y, m, d] = startDate.split("-").map(Number);
    const arr = [];
    for (let i = 0; i < taksitSayisi; i++) {
      const dt = new Date(y, m - 1 + i, d);
      arr.push({
        tarih: dt.toISOString().slice(0, 10),
        tutar: i === taksitSayisi - 1 ? rem : per,
        odendi: false,
      });
    }
    setForm({ ...form, taksitler: arr });
    toast.success(`${taksitSayisi} taksit oluşturuldu (aylık ${formatMoney(per, form.currency)})`);
  };

  const addInstallment = () => {
    setForm({
      ...form,
      taksitler: [...form.taksitler, { tarih: new Date().toISOString().slice(0, 10), tutar: 0, odendi: false }],
    });
  };
  const updateInstallment = (i, field, val) => {
    const copy = [...form.taksitler];
    copy[i] = { ...copy[i], [field]: val };
    setForm({ ...form, taksitler: copy });
  };
  const removeInstallment = (i) => {
    setForm({ ...form, taksitler: form.taksitler.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Krediler</h1>
          <p className="text-sm text-muted-foreground mt-1">Araç ve diğer kredilerin taksit takibi.</p>
        </div>
        <Button onClick={openNew} data-testid="add-loan-btn"><Plus size={16} className="mr-2" /> Yeni Kredi</Button>
      </div>

      {loans.length === 0 && (
        <Card className="border-border"><CardContent className="p-8 text-center text-muted-foreground">
          Kredi kaydı bulunmuyor. Sağ üstteki "Yeni Kredi" butonundan ekleyebilirsiniz.
        </CardContent></Card>
      )}

      {loans.map((l) => {
        const total = l.taksitler.length;
        const paid = l.taksitler.filter((t) => t.odendi).length;
        const remaining = total - paid;
        const totalAmt = l.taksitler.reduce((a, b) => a + b.tutar, 0);
        const paidAmt = l.taksitler.filter((t) => t.odendi).reduce((a, b) => a + b.tutar, 0);
        const remAmt = totalAmt - paidAmt;
        const pct = totalAmt > 0 ? Math.round((paidAmt / totalAmt) * 100) : 0;
        return (
          <Card key={l.id} className="border-border" data-testid={`loan-card-${l.id}`}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-secondary text-primary"><Car size={22} weight="duotone" /></div>
                <div>
                  <CardTitle className="font-heading">{l.ad}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {l.banka} {l.iban && <span className="tabular-nums text-xs">· {l.iban}</span>}
                  </p>
                  {l.aciklama && <p className="text-xs text-muted-foreground">{l.aciklama}</p>}
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`edit-loan-${l.id}`}><PencilSimple size={15} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(l.id)} data-testid={`delete-loan-${l.id}`}><Trash size={15} className="text-destructive" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-border">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Toplam Kredi</div>
                  <div className="font-heading font-bold text-lg tabular-nums" data-testid={`loan-total-${l.id}`}>{formatMoney(totalAmt, l.currency)}</div>
                  <div className="text-xs text-muted-foreground">{total} taksit</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Ödenen</div>
                  <div className="font-heading font-bold text-lg tabular-nums urgency-green" data-testid={`loan-paid-${l.id}`}>{formatMoney(paidAmt, l.currency)}</div>
                  <div className="text-xs text-muted-foreground">{paid} taksit</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Kalan</div>
                  <div className="font-heading font-bold text-lg tabular-nums urgency-red" data-testid={`loan-remaining-${l.id}`}>{formatMoney(remAmt, l.currency)}</div>
                  <div className="text-xs text-muted-foreground">{remaining} taksit</div>
                </div>
                <div data-testid={`loan-progress-${l.id}`}>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">İlerleme</div>
                  <div className="font-heading font-bold text-lg tabular-nums">%{pct}</div>
                  <Progress value={pct} className="mt-1 h-2" />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {l.taksitler.map((t, i) => {
                  const d = daysUntil(t.tarih);
                  return (
                    <div key={i} className={`p-3 rounded-md border ${t.odendi ? "bg-secondary/50 opacity-60" : "bg-card"} border-border`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground">Taksit #{i + 1}</div>
                        <Checkbox checked={t.odendi} onCheckedChange={() => toggle(l, i)} data-testid={`installment-${l.id}-${i}`} />
                      </div>
                      <div className="text-sm font-medium tabular-nums">{formatDate(t.tarih)}</div>
                      <div className="text-sm tabular-nums">{formatMoney(t.tutar, l.currency)}</div>
                      {!t.odendi && <Badge className={`mt-1 text-[10px] ${urgencyClass(d)}`}>{d} gün</Badge>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Krediyi Düzenle" : "Yeni Kredi"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Şirket</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="loan-form-company"><SelectValue placeholder="Seç..." /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Para Birimi</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["TRY","USD","EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Kredi Adı</Label>
              <Input data-testid="loan-form-ad" value={form.ad} onChange={(e) => setForm({ ...form, ad: e.target.value })} />
            </div>
            <div>
              <Label>Banka</Label>
              <Input value={form.banka} onChange={(e) => setForm({ ...form, banka: e.target.value })} />
            </div>
            <div>
              <Label>Hesap No / IBAN</Label>
              <IbanInput data-testid="loan-form-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Açıklama</Label>
              <Input value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} />
            </div>
          </div>

          {!editingId && (
            <div className="mt-4 p-4 rounded-md bg-secondary/30 border border-border space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold"><Sparkle size={16} weight="duotone" className="text-primary" /> Otomatik Taksit Üret</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Toplam Kredi Tutarı</Label>
                  <MoneyInput data-testid="auto-total" value={totalAmount} onChange={(n) => setTotalAmount(n)} />
                </div>
                <div>
                  <Label className="text-xs">Taksit Sayısı</Label>
                  <Select value={String(taksitSayisi)} onValueChange={(v) => setTaksitSayisi(Number(v))}>
                    <SelectTrigger data-testid="auto-count"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TAKSIT_SAYILARI.map((n) => <SelectItem key={n} value={String(n)}>{n} taksit</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">İlk Taksit Tarihi</Label>
                  <Input data-testid="auto-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={generateInstallments} data-testid="auto-generate-btn">
                <Sparkle size={14} className="mr-1" /> {taksitSayisi} Taksite Böl ve Oluştur
              </Button>
              {totalAmount > 0 && (
                <div className="text-xs text-muted-foreground">
                  Aylık taksit: <b>{formatMoney(totalAmount / taksitSayisi, form.currency)}</b> · Son taksit yuvarlama farkını içerir.
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Taksitler ({form.taksitler.length})</Label>
              <Button size="sm" variant="outline" onClick={addInstallment} data-testid="add-installment-btn"><PlusCircle size={14} className="mr-1" /> Taksit Ekle</Button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {form.taksitler.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">
                  Henüz taksit yok. Yukarıdan otomatik üretebilir veya "Taksit Ekle" ile tek tek ekleyebilirsiniz.
                </div>
              )}
              {form.taksitler.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 text-sm text-muted-foreground text-center">#{i + 1}</div>
                  <Input className="col-span-4" type="date" value={t.tarih} onChange={(e) => updateInstallment(i, "tarih", e.target.value)} data-testid={`inst-date-${i}`} />
                  <div className="col-span-4">
                    <MoneyInput value={t.tutar}
                      onChange={(n) => updateInstallment(i, "tutar", n)} data-testid={`inst-amount-${i}`} />
                  </div>
                  <label className="col-span-2 flex items-center gap-2 text-sm">
                    <Checkbox checked={t.odendi} onCheckedChange={(v) => updateInstallment(i, "odendi", !!v)} />
                    Ödendi
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => removeInstallment(i)} className="col-span-1">
                    <Trash size={14} className="text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} data-testid="loan-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
